import { HttpClient, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, map, Observable, tap } from 'rxjs';
import { RequestUserModel, UserModel, UserSessionModel } from '../Models/user.model';
import { environment } from '../environments/environment.prod';
import { ToastrService } from 'ngx-toastr';

@Injectable({
    providedIn: 'root',
})
export class LoginService {
    private apiUrl = environment.apiUrl;
    private authUrl = environment.authUrl;
    private currentUserSubject = new BehaviorSubject<UserSessionModel | null>(
        null
    );
    public currentUser$ = this.currentUserSubject.asObservable();
    private heartBeat: any = null;

    constructor(
        private http: HttpClient, 
        private router: Router,
        private toastr: ToastrService
    ) {
        const user = this.getUser();
        if (user) {
            this.currentUserSubject.next(user);
        }
    }

    /**
     * Logs in a user by sending a POST request to the server with email and password.
     * @param email - The email of the user trying to log in.
     * @param password - The password of the user trying to log in.
     * @returns Observable<{ user: string }> - An observable that emits the user information after a successful login.
     */
    public login(
        email: string,
        password: string
    ): Observable<{ user: string }> {
        return this.http
            .post<{ user: string }>(
                `${this.authUrl}/login`,
                { email, password },
                {
                    withCredentials: true,
                }
            )
            .pipe(
                tap((response) => {
                    sessionStorage.setItem(
                        'user',
                        JSON.stringify(response.user)
                    );
                    this.startHeartbeat();
                })
            );
    }

    /**
     * Function starts a schedule to check the session token of the logged in user
     * @returns {void} 
     */
    public startHeartbeat(): void {
        if (this.heartBeat || !this.isLoggedIn()) {
            return;
        }

        this.heartBeat = setInterval(() => {
            this.http.get(`${this.apiUrl}/pingAuth`, {
                withCredentials: true
            }).subscribe({
                next: () => {
                    console.log("Still valid")
                }, // session is still valid
                error: (error) => {
                    if (error.status === 401) {
                        this.toastr.info("You have been logged out", "Session Expired");
                        this.logout();
                    }
                }
            })
        }, 15 * 60 * 1000); // every 15 mins
    }

    /**
     * Function stops the interval check
     */
    public stopHeartbeat(): void {
        if (this.heartBeat) {
            clearInterval(this.heartBeat);
            this.heartBeat = null;
        }
    }

    /**
     * Function obtains the user account request object and parses it into the 
     * Cloudflare worker to be added to the database
     * @param {RequestUserModel} reqUser The requested account data object
     * @returns {Observable<HttpResponse<any>>} An observable that emits an http response from the cloudflare worker
     */
    public requestAccess(reqUser: RequestUserModel): Observable<HttpResponse<any>> {
        return this.http.post<any>(`${this.apiUrl}/userRequest`,
            reqUser,
            {
                withCredentials: true
            }
        )
    }

    /**
     * Logs out the current user by sending a POST request to the server and clearing the session storage.
     */
    public logout(): void {
        this.stopHeartbeat();
        this.http
            .post(`${this.authUrl}/logout`, {}, { withCredentials: true })
            .subscribe(() => {
                sessionStorage.removeItem('user');
                this.router.navigate(['/login']);
            });
    }

    /**
     * Checks if a user is currently logged in by checking the session storage.
     * @returns boolean - Returns true if a user is logged in, false otherwise.
     */
    public isLoggedIn(): boolean {
        return !!sessionStorage.getItem('user');
    }

    /**
     * Sets the current user in the session storage and updates the BehaviorSubject.
     * @param user - The user object to be set as the current user.
     */
    public setUser(user: UserSessionModel): void {
        sessionStorage.setItem('user', JSON.stringify(user));
        this.currentUserSubject.next(user);
    }

    /**
     * Retrieves the current user from the session storage.
     * @returns UserSessionModel | null - Returns the current user if available, otherwise null.
     */
    public getUser(): UserSessionModel | null {
        const raw = sessionStorage.getItem('user');
        if (!raw) {
            return null;
        }

        try {
            const parsed = JSON.parse(raw);
            var user: UserSessionModel = {
                user_title: parsed.user_title,
                role: parsed.role,
                bp_code: parsed.bp_code,
                company: parsed.company
            };
            return user;
        } catch (err) {
            console.error('Error parsing user from session', err);
            return null;
        }
    }

    /**
     * Retrieves a list of all users from the server.
     * @returns Observable<UserModel[]> - An observable that emits an array of user models.
     */
    public getAllUsers(): Observable<UserModel[]> {
        return this.http
            .get<{ users: UserModel[] }>(`${this.apiUrl}/getUsers`, {
                withCredentials: true,
            })
            .pipe(map((response) => response.users));
    }

    /**
     * Obtains an array of all requests for accounts
     * @returns {Observable<RequestUserModel[]>} The array of requested users
     */
    public getAllRequestedUsers(): Observable<RequestUserModel[]> {
        return this.http.get<{ requested_users: RequestUserModel[] }>(`${this.apiUrl}/getRequestedUsers`, {
            withCredentials: true,
        })
        .pipe(map((response) => response.requested_users));
    }

    /**
     * Retrieves a user by their ID from the server.
     * @param user - The user object containing the user ID.
     * @returns Observable<HttpResponse<any>> - An observable that emits the HTTP response containing the user data.
     */
    public addNewUser(user: UserModel): Observable<HttpResponse<any>> {
        return this.http.post<any>(`${this.apiUrl}/addUser`, user, {
            observe: 'response',
            withCredentials: true,
        });
    }

    /**
     * Updates an existing user in the database.
     * @param user - The user object containing the updated user information.
     * @returns Observable<HttpResponse<any>> - An observable that emits the HTTP response after updating the user.
     */
    public editUser(user: {
        user_id: number | null;
        email: string;
        password: string | null;
        user_title: string;
        role: string;
        bp_code: string;
        company: string;
    }): Observable<HttpResponse<any>> {
        return this.http.patch<any>(`${this.apiUrl}/updateUser`, user, {
            observe: 'response',
            withCredentials: true,
        });
    }

    /**
     * Deletes a user from the database.
     * @param user - The user object to be deleted.
     * @returns Observable<HttpResponse<any>> - An observable that emits the HTTP response after deleting the user.
     */
    public deleteUser(user: UserModel): Observable<HttpResponse<any>> {
        return this.http.delete<any>(`${this.apiUrl}/removeUser`, {
            body: {
                user_id: user.user_id,
                email: user.email,
            },
            observe: 'response',
            withCredentials: true,
        });
    }
}
